import core from './core';
import FormatPrice from './mixins/format-price';
import { cssClass } from '../helpers/dom-interact';
import { SentryLogger } from '../helpers/Sentry';

const PriceCalculator = core.extend({
	constructor(opts) {
		if (!opts || !opts.totalHolder || !opts.data) {
			throw new Error('params are missing');
		}

		this.init(
			opts.totalHolder,
			opts.priceHolder,
			opts.data,
			opts.quantity,
			opts.tooltipList,
			opts.openTooltipOnPriceChange
		);
	},

	init(totalHolder, priceHolder, data, qty, tooltipList = [], openTooltipOnPriceChange = false) {
		this.cachedPrice = -1;

		this.totalHolder = totalHolder;

		this.priceHolder = priceHolder;

		this.tooltipList = tooltipList;

		this.openTooltipOnPriceChange = openTooltipOnPriceChange;

		const quantity = qty || data.preConfig.quantity;
		const productPreConfig = data.concreteProducts.find((p) => p.format === data.preConfig.format);
		const product = productPreConfig || data.concreteProducts[0];
		const price = this.getPrice(product.prices, quantity);

		this.data = {
			// default data
			price,
			product,
			quantity,
			locale: data.locale || 'de_DE',
			productOptions: {},
			baseTaxPercentage: data.baseTaxPercentage,
			taxPercentage: data.taxPercentage,
			currencyRate: data.currencyRate,
		};

		if (price && price.isSample && price.isSampleFree) {
			this.showFreeSamplesInfoButton();
		}
	},

	setSelectedOpt(obj) {
		const optionsMapping = {
			color: 'color',
			paper: 'papers',
			tag: 'tags',
			diecut: 'diecuts',
			refinement: 'refinements',
			woodenBlock: 'woodenBlocks',
			frame: 'frames',
			printQuality: 'printQuality',
			amountOfPhotos: 'amountsOfPhotos',
			box: 'box',
			outerPaper: 'outerPapers',
			ribbon: 'ribbon',
			numberOfPages: 'numberOfPages',
			innerPages: 'innerPages',
			premiumDesign: 'premiumDesign',
		};

		obj = _.pick(obj, Object.keys(optionsMapping));

		for (const option in obj) {
			const productOptions = this.data.product.productOptions[optionsMapping[option]];

			if (productOptions) {
				const currentOptArray = _.find(productOptions, (opt) => opt.key === obj[option]);

				this.data.productOptions[`${option}Price`] = currentOptArray
					? _.find(productOptions, (opt) => opt.key === obj[option]).price
					: 0;
			}
		}
	},

	setActiveProduct(product) {
		this.data.product = product;

		this.resetPricesOnProductOptions(product.productOptions);

		this.update();
	},

	resetPricesOnProductOptions(productOptions) {
		const possibleDataKeysAndMapping = {
			colors: 'colorPrice',
			diecuts: 'diecutPrice',
			tags: 'tagPrice',
			refinements: 'refinementPrice',
			frames: 'framePrice',
			amountsOfPhotos: 'amountOfPhotosPrice',
			box: 'boxPrice',
			papers: 'paperPrice',
			outerPapers: 'outerPaperPrice',
			printQualities: 'printQualityPrice',
			woodenBlocks: 'woodenBlockPrice',
			numberOfPages: 'numberOfPagesPrice',
			innerPages: 'innerPagesPrice',
			premiumDesign: 'premiumDesignPrice',
		};

		_.each(Object.keys(possibleDataKeysAndMapping), (key) => {
			if (!(productOptions[key] || []).length) {
				this.data.productOptions[possibleDataKeysAndMapping[key]] = 0;
			} else {
				this.data.productOptions[possibleDataKeysAndMapping[key]] = productOptions[key][0].price;
			}
		});
	},

	refreshData(obj) {
		this.data.quantity = typeof obj.quantity !== 'undefined' ? obj.quantity : this.data.quantity || 5;

		this.setProductOptionsPrices(obj);

		this.data.price = this.getPrice(this.data.product.prices, this.data.quantity);
	},

	setProductOptionsPrices(obj) {
		const productOptionsWithPrice = [
			'colorPrice',
			'paperPrice',
			'diecutPrice',
			'refinementPrice',
			'tagPrice',
			'framePrice',
			'amountOfPhotosPrice',
			'boxPrice',
			'woodenBlockPrice',
			'outerPaperPrice',
			'printQualityPrice',
			'numberOfPagesPrice',
			'innerPagesPrice',
			'premiumDesignPrice',
		];

		_.each(productOptionsWithPrice, (key) => {
			this.data.productOptions[key] =
				typeof obj[key] !== 'undefined' ? obj[key] : this.data.productOptions[key] || 0;
		});
	},

	update(obj) {
		this.refreshData(obj || {});

		const { locale, quantity, price: priceData } = this.data;

		if (!priceData) {
			SentryLogger.getInstance().sendInfoReport({
				message: `Incorrect data: price is not defined for ${this.data.product.concreteSku}`,
				extra: {
					extraMessage: 'product concreteSku',
					value: [this.data.product.concreteSku],
				},
			});
		}

		const { isSample, isSampleFree } = priceData;
		const isGoogleShopping = document.body.classList.contains('is-google-shopping');

		let { price } = priceData;

		if (!isSample) {
			price += this.getExtras();
			this.hideFreeSamplesInfoButton();
		} else if (isSampleFree && !isGoogleShopping) {
			// for google shopping prices should be the same as for regular product
			price = 0;
		}

		// prevent opening tooltip after options change
		if (+this.cachedPrice !== 0 && isSampleFree) {
			this.showFreeSamplesInfoButton();
		}

		if (this.priceHolder) {
			this.priceHolder.html(this.format(this.calculateFinalPrice(price), locale));
		}

		const totalPrice = this.calculateFinalPrice(price, +quantity);

		// for sample product price is fixed but rest of the prices in select should be updated
		if (this.cachedPrice === totalPrice && !isSample) {
			return false;
		}

		this.cachedPrice = totalPrice;

		this.totalHolder.html(this.format(totalPrice, locale));

		return true;
	},

	/*
	 * Same implementation as in Price::getInStoreCurrencyIncludingTax
	 * Both JS and PHP should always calculate the same prices, so if you need
	 * to make any logical changes here, please make sure they also get applied
	 * in the PHP class!
	 */
	calculateFinalPrice(price, quantity = 1) {
		let finalPrice;

		const totalSinglePrice = ((price / 100) * this.data.currencyRate).toFixed(2);
		const totalPrice = totalSinglePrice * quantity;

		if (this.data.baseTaxPercentage !== this.data.taxPercentage) {
			const baseTaxAmount = totalPrice * (1 - 1 / (1 + this.data.baseTaxPercentage / 100));
			const totalPriceWithoutTax = +(totalPrice - baseTaxAmount).toFixed(2);
			const rowTax = totalPriceWithoutTax * (this.data.taxPercentage / 100);

			finalPrice = totalPriceWithoutTax + rowTax;
		} else {
			finalPrice = totalPrice;
		}

		return finalPrice.toFixed(2);
	},

	getExtras() {
		return _.reduce(this.data.productOptions, (extras, option) => extras + option, 0);
	},

	getSamplePrice() {
		const samplePriceObj = this.data.product.prices[0];
		const samplePrice = samplePriceObj.isSampleFree ? 0 : +samplePriceObj.price;

		return this.format(this.calculateFinalPrice(samplePrice), this.data.locale);
	},

	getPrice(prices, quantity) {
		return prices.find((priceObj) => +priceObj.quantity === +quantity);
	},

	hideFreeSamplesInfoButton() {
		this.tooltipList.forEach(({ holder }) => cssClass.remove(holder, ['has-free-sample']));
	},

	showFreeSamplesInfoButton() {
		this.tooltipList.forEach((tooltip) => {
			cssClass.add(tooltip.holder, ['has-free-sample']);
			if (this.openTooltipOnPriceChange) {
				setTimeout(() => {
					tooltip.openTooltip();
				}, 1000);
			}
		});
	},
});

PriceCalculator.include(FormatPrice);

export default PriceCalculator;
